import { Radio } from "@faire/slate/components/radio/Radio";
import { buildId } from "@faire/slate/lib/idUtils";
import { componentGroupCls } from "@faire/slate/lib/tracking";
import { Fieldset } from "@faire/slate/private/form/Fieldset";
import type { FormGroupLabelVariant } from "@faire/slate/private/form/types";
import { buildFormIds } from "@faire/slate/private/form/utils";
import type { IText, IOption } from "@faire/slate/private/shared/types";
import React from "react";

import { RADIO_GROUP_UNIQUE_NAME_PREFIX } from "./consts";

type IProps<ValueType extends string> = {
  /**
   * Controls which radio is checked.
   */
  checked?: ValueType;

  /**
   * Whether all radios in the radio group are disabled.
   */
  disabled?: boolean;

  /**
   * Controls the error state of the radio group.
   */
  error?: IText;

  /**
   * The radio group label.
   */
  label?: string;

  /**
   * The font weight of the radio group header label.
   *
   * @default "regular"
   */
  labelVariant?: FormGroupLabelVariant;

  /**
   * The radio options in the radio group.
   */
  options: Array<IOption<ValueType>>;

  /**
   * **web**
   * Whether the radio group should be autofocused.
   */
  autoFocus?: HTMLInputElement["autofocus"];

  /**
   * **web**
   * The id of the radio group.
   */
  id?: string;

  /**
   * **web**
   * The name of the radio group, used to group the radios.
   */
  name?: string;

  /**
   * **web**
   * The callback when a radio is checked.
   */
  onChecked?: (option: IOption<ValueType>) => void;

  // DO NOT ADD any new prop without aligning with Slate team first
};

/**
 * RadioGroups enable users to select one option from a list.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * {@link https://www.notion.so/faire/Radio-2242efb5c25a80e0acc3c4bff1d737de | Slate/RadioGroup}
 */
export const RadioGroup = <ValueType extends string = string>({
  autoFocus,
  checked,
  disabled,
  error,
  label,
  labelVariant = "regular",
  options,
  id: idProp,
  name,
  onChecked,
  ...props
}: IProps<ValueType>) => {
  const { id, helperId } = buildFormIds({
    prefix: RADIO_GROUP_UNIQUE_NAME_PREFIX,
    idProp,
    name,
    label,
    error,
  });

  const uniqueName = name ?? buildId(RADIO_GROUP_UNIQUE_NAME_PREFIX, id);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const option = options.find(({ value }) => value === e.target.value);
    if (option) {
      onChecked?.(option);
    }
  };

  return (
    <Fieldset
      className={componentGroupCls()}
      data-component-name="Slate/RadioGroup"
      disabled={disabled}
      error={error}
      gap={4}
      label={label}
      labelVariant={labelVariant}
      helperId={helperId}
      optional={false}
      {...props}
    >
      <div className="flex flex-col gap-(--slate-spacing-element-medium)">
        {options.map((option, i) => {
          const checkedDefault = onChecked ? false : undefined;
          return (
            <Radio
              id={`${uniqueName}-${option.value}`}
              {...option}
              // NOTE(alipianu): autofocusing the radio group means autofocusing the first radio
              autoFocus={!i ? autoFocus : undefined}
              checked={
                checked !== undefined
                  ? checked === option.value
                  : checkedDefault
              }
              disabled={disabled}
              key={option.value}
              name={uniqueName}
              onChange={onChecked ? handleChange : undefined}
            />
          );
        })}
      </div>
    </Fieldset>
  );
};
